/**
 * "You're near the Dome — now: Shpongle · next: Tündérke at 22:00" (plan.md step 60).
 *
 * On by default (2026-10-07; the account's settings turn it off), festival mode only. **Location is read on this phone, used
 * on this phone, and never sent**: the stage positions come from the stored
 * bundle (map pins through the inverse of the site's GPS warp —
 * `stagePlaces`), the decision is `proximityDecision` (once per arrival, a
 * cooldown, quiet hours, a daily cap), and the message is built from the
 * bundle's own schedule. Low-power position, only while the app is open; a
 * background geofence would need the "Always" permission and is a separate
 * decision (plan.md step 60, task 4).
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/app/AuthProvider';
import {
  DEFAULT_PROXIMITY, DEFAULT_STAGE_ALERT_RADIUS_M, EMPTY_PROXIMITY, liveByStage, nearestStage, proximityDecision, stagePlaces, wallClock,
  type NotificationSettings, type ProximityState,
} from '@myrave/shared';
import { useEdition } from '@/app/EditionProvider';
import { readJson, writeJson } from '@/lib/storage';
import { useT } from '@/i18n';
import { useToast } from '@/ui';
import { noteNearStageForTickets } from './tickets/ticketRules';
import './proximity.css';

interface Setting {
  on: boolean; radiusM: number;
  /** The account's quiet hours, when a live rave does NOT override them (step 60). */
  quietFrom?: string | null; quietTo?: string | null; quietTimeZone?: string | null;
}
/**
 * ON at 100 m for everyone (the user, 2026-10-07) — the account's own choice
 * where there is one (`GET /me/notification-settings`, set on the account
 * page), kept on the phone (`myrave:platform:stage-alerts`) for the moment
 * there is no signal, which at a festival is most moments.
 */
const DEFAULT: Setting = { on: true, radiusM: DEFAULT_STAGE_ALERT_RADIUS_M };
const KEPT = 'stage-alerts';

export function useStageAlertSetting(): Setting {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['me', 'notification-settings'],
    queryFn: () => api.get<{ settings: NotificationSettings }>('/me/notification-settings'),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const fresh = q.data?.settings;
  const mine = useMemo(() => (fresh ? stageAlertSetting(fresh) : null), [fresh]);
  useEffect(() => {
    if (mine) writeJson(null, KEPT, mine);
  }, [mine]);
  return mine ?? readJson<Setting>(null, KEPT, DEFAULT);
}

/** What the watcher needs of the account's settings — quiet hours only when a live rave does not override them. */
export function stageAlertSetting(s: NotificationSettings): Setting {
  const quiet = !s.festivalOverridesQuiet && s.quietFrom && s.quietTo;
  return {
    on: s.stageAlerts, radiusM: s.stageAlertRadiusM,
    ...(quiet ? { quietFrom: s.quietFrom, quietTo: s.quietTo, quietTimeZone: s.quietTimeZone } : {}),
  };
}

/** The watcher: mounted for the whole rave in festival mode, silent unless switched on. */
/**
 * The last "you're near a stage" — told as a toast everywhere, and drawn as a
 * card at the top of Live while it is fresh (the Stitch design, 2026-10-08).
 * In memory only: it is about where the phone is right now.
 */
export interface NearStage { stageId: string; name: string; color: string | null; metres: number; now: string | null; at: number }
let nearStage: NearStage | null = null;
const nearListeners = new Set<() => void>();
export function setNearStage(next: NearStage | null): void {
  nearStage = next;
  for (const cb of nearListeners) cb();
}
export function useNearStage(): NearStage | null {
  return useSyncExternalStore(
    (cb) => { nearListeners.add(cb); return () => { nearListeners.delete(cb); }; },
    () => nearStage,
    () => null,
  );
}

/**
 * Where the phone is, from the stage alerts' own reading — so Live's cards can
 * say how far each stage is and how long the walk (2026-10-30). In memory only,
 * like `nearStage`: never kept, never sent. Nothing when the alerts are off,
 * because then nothing reads the position at all.
 */
export interface Here { lat: number; lng: number; at: number }
let here: Here | null = null;
const hereListeners = new Set<() => void>();
export function setHere(next: Here | null): void {
  here = next;
  for (const cb of hereListeners) cb();
}
export function useHere(): Here | null {
  return useSyncExternalStore(
    (cb) => { hereListeners.add(cb); return () => { hereListeners.delete(cb); }; },
    () => here,
    () => null,
  );
}

/**
 * The stage this phone was last near, per rave (`myrave:{ed}:lastStage`) —
 * the always-on card's "the stage I was last near" (step 67). Kept, unlike
 * `nearStage`: the card is still up after the app is closed. Written on every
 * fix that is near a stage, not only on an arrival the toast tells.
 */
const lastStageListeners = new Set<() => void>();
export function rememberLastStage(editionId: string, stageId: string): void {
  if (readJson<string | null>(editionId, 'lastStage', null) === stageId) return;
  writeJson(editionId, 'lastStage', stageId);
  for (const cb of lastStageListeners) cb();
}
export function useLastStage(editionId: string): string | null {
  return useSyncExternalStore(
    (cb) => { lastStageListeners.add(cb); return () => { lastStageListeners.delete(cb); }; },
    () => readJson<string | null>(editionId, 'lastStage', null),
    () => null,
  );
}

export function useStageProximity(enabled: boolean): void {
  const { bundle, acts, stages, timezone } = useEdition();
  const tr = useT();
  const toast = useToast();
  const editionId = bundle.edition.id;
  const setting = useStageAlertSetting();
  const places = useMemo(() => stagePlaces(bundle.map, stages), [bundle.map, stages]);
  const { gatesOpenAt, endsAt } = bundle.edition;

  useEffect(() => {
    if (!enabled || !setting.on || !places?.length || typeof navigator === 'undefined' || !navigator.geolocation) return;
    const settings = {
      ...DEFAULT_PROXIMITY, radiusM: setting.radiusM,
      quietFrom: setting.quietFrom ?? null, quietTo: setting.quietTo ?? null, quietTimeZone: setting.quietTimeZone ?? null,
    };
    const id = navigator.geolocation.watchPosition((p) => {
      const now = new Date();
      setHere({ lat: p.coords.latitude, lng: p.coords.longitude, at: now.getTime() });
      const near = nearestStage(places, { lat: p.coords.latitude, lng: p.coords.longitude }, settings.radiusM);
      if (near) {
        rememberLastStage(editionId, near.place.stageId);
        // Near a stage once: the raver is inside — festival mode's Ticket button goes for good
        // (step 82 N2). Only the moment is kept, never the position.
        noteNearStageForTickets(editionId, { gatesOpenAt, endsAt }, timezone, now);
      }
      const state = readJson<ProximityState>(editionId, 'proximityState', EMPTY_PROXIMITY);
      const decided = proximityDecision(state, near?.place ?? null, now, settings, timezone);
      writeJson(editionId, 'proximityState', decided.state);
      if (!decided.notify) return;
      const live = liveByStage(acts, now).find((s) => s.stageId === decided.notify!.stageId);
      setNearStage({
        stageId: decided.notify.stageId, name: decided.notify.name,
        color: stages.find((st) => st.id === decided.notify!.stageId)?.color ?? null,
        metres: near?.metres ?? settings.radiusM, now: live?.now?.displayName ?? null, at: now.getTime(),
      });
      const parts = [
        live?.now ? tr('now: {act}', { act: live.now.displayName }) : null,
        live?.next ? tr('next: {act} at {time}', { act: live.next.displayName, time: wallClock(new Date(live.next.startAt), timezone) }) : null,
      ].filter(Boolean).join(' · ');
      toast.show(parts ? tr("You're near {stage} — {what}", { stage: decided.notify.name, what: parts }) : tr("You're near {stage}", { stage: decided.notify.name }));
    }, () => undefined, { enableHighAccuracy: false, maximumAge: 30_000, timeout: 60_000 });
    return () => { navigator.geolocation.clearWatch(id); setHere(null); };
  }, [enabled, setting.on, setting.radiusM, setting.quietFrom, setting.quietTo, setting.quietTimeZone, places, acts, stages, editionId, timezone, toast, tr, gatesOpenAt, endsAt]);
}
